/**
 * Version of the cookie-consent texts. Bump it whenever a category's purpose
 * changes materially: a stored decision for an older version is treated as
 * "no decision", so the banner asks again (informed consent).
 * 2026-11-01-v2: "Analytics" now covers PostHog EU incl. session replay and
 * the (now consent-gated) Portfolio Analytics tracker.
 */
export const CONSENT_VERSION = '2026-11-01-v2'

export const pendingStateCookieName = 'ae_pending_v1'
export const pendingStateStorageName = 'ae_pending_full_v1'
export const consentCookieName = 'ae_cookie_consent'
export const mediaConsentCookieName = 'ae_media_consent'
export const analyticsConsentCookieName = 'ae_analytics_consent'
export const gpcHonoredCookieName = 'ae_gpc_honored'
// Readable (NON-httpOnly) cookie the SSR request middleware sets to '1' when the
// incoming request carried the `Sec-GPC: 1` header (Brave / DuckDuckGo / Firefox
// with GPC enabled). The `Sec-GPC` header is the authoritative, always-present
// GPC signal; `navigator.globalPrivacyControl` is exposed inconsistently across
// browsers (notably absent on Brave for the JS property in some configs).
// Mirroring the header into this client-readable cookie lets the consent hook
// honour GPC reliably on the very first paint, before any JS-property check.
export const gpcSignalCookieName = 'ae_gpc'
export const themeCookieName = 'ae_theme'
export const anonIdCookieName = 'ae_anon_id'
// Last-visit timestamp (ms) for anonymous visitors, used to compute "new since
// your last visit" on the Progress dashboard without any account.
export const lastVisitStorageName = 'ae_last_visit_v1'

export type ConsentState = {
  version: string
  necessary: true
  analytics: boolean
  externalMedia: boolean
  marketing: boolean
}

type TargetSet = {
  lessons: Array<string>
  courses: Array<string>
  resources: Array<string>
  posts: Array<string>
  changelog: Array<string>
}

type LikeSet = {
  lessons: Array<string>
  comments: Array<string>
  posts: Array<string>
  resources: Array<string>
  changelog: Array<string>
}

type ProgressItem = {
  watchedSeconds: number
  completed: boolean
  lastViewedAt: number
}

// Per-day learning activity for anonymous users, keyed by UTC `YYYY-MM-DD`. This
// mirrors the server `learningActivity` table so streak math is identical for
// signed-out and signed-in visitors, and migrates cleanly on login.
type DailyActivity = {
  minutesLearned: number
  lessonsCompleted: number
}

export type AnonymousState = {
  saved: TargetSet
  likes: LikeSet
  progress: Record<string, ProgressItem>
  activity: Record<string, DailyActivity>
}

/*
 * Validation of untrusted cookie / localStorage JSON.
 *
 * Hand-written instead of zod (M6 performance): this module is in the eager
 * browser bundle of EVERY page, and even `zod/mini` added ~15 KB brotli (its
 * core schemas) for two small shapes. Semantics are the ones the zod schemas
 * had, pinned by anonymous-state.test.ts: objects must be plain objects,
 * unknown keys are dropped, a missing (`undefined`) field takes its default,
 * any wrong type rejects the WHOLE value, numbers must be finite and >= 0.
 */
class Invalid extends Error {}

function fail(): never {
  throw new Invalid()
}

function plainObject(input: unknown): Record<string, unknown> {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) fail()
  return input as Record<string, unknown>
}

function withDefault<T>(value: unknown, parse: (v: unknown) => T, fallback: () => T): T {
  return value === undefined ? fallback() : parse(value)
}

function stringArray(input: unknown): Array<string> {
  if (!Array.isArray(input)) fail()
  return input.map((item) => (typeof item === 'string' ? item : fail()))
}

function nonNegative(input: unknown): number {
  return typeof input === 'number' && Number.isFinite(input) && input >= 0
    ? input
    : fail()
}

function bool(input: unknown): boolean {
  return typeof input === 'boolean' ? input : fail()
}

function record<T>(input: unknown, parse: (v: unknown) => T): Record<string, T> {
  const source = plainObject(input)
  const out: Record<string, T> = {}
  for (const key of Object.keys(source)) out[key] = parse(source[key])
  return out
}

function stringLists<TKey extends string>(
  input: unknown,
  keys: ReadonlyArray<TKey>,
): Record<TKey, Array<string>> {
  const source = plainObject(input)
  const out = {} as Record<TKey, Array<string>>
  for (const key of keys) out[key] = withDefault(source[key], stringArray, () => [])
  return out
}

const TARGET_KEYS = ['lessons', 'courses', 'resources', 'posts', 'changelog'] as const
const LIKE_KEYS = ['lessons', 'comments', 'posts', 'resources', 'changelog'] as const

function progressItem(input: unknown): ProgressItem {
  const source = plainObject(input)
  return {
    watchedSeconds: withDefault(source.watchedSeconds, nonNegative, () => 0),
    completed: withDefault(source.completed, bool, () => false),
    lastViewedAt: withDefault(source.lastViewedAt, nonNegative, () => 0),
  }
}

function dailyActivity(input: unknown): DailyActivity {
  const source = plainObject(input)
  return {
    minutesLearned: withDefault(source.minutesLearned, nonNegative, () => 0),
    lessonsCompleted: withDefault(source.lessonsCompleted, nonNegative, () => 0),
  }
}

function validateAnonymousState(input: unknown): AnonymousState {
  const source = plainObject(input)
  return {
    saved: withDefault(
      source.saved,
      (v) => stringLists(v, TARGET_KEYS),
      () => stringLists({}, TARGET_KEYS),
    ),
    likes: withDefault(
      source.likes,
      (v) => stringLists(v, LIKE_KEYS),
      () => stringLists({}, LIKE_KEYS),
    ),
    progress: withDefault(source.progress, (v) => record(v, progressItem), () => ({})),
    activity: withDefault(source.activity, (v) => record(v, dailyActivity), () => ({})),
  }
}

/** Current-version consent decision, or throws {@link Invalid}. */
function validateConsent(input: unknown): ConsentState {
  const source = plainObject(input)
  if (source.version !== CONSENT_VERSION || source.necessary !== true) fail()
  return {
    version: CONSENT_VERSION,
    necessary: true,
    analytics: bool(source.analytics),
    externalMedia: bool(source.externalMedia),
    marketing: bool(source.marketing),
  }
}

export type SavedTargetGroup = keyof AnonymousState['saved']
export type LikedTargetGroup = keyof AnonymousState['likes']

/**
 * Looser input shape for the pure helpers below. They all re-parse their input
 * through {@link parseAnonymousState} (which applies schema defaults), so a
 * caller may pass state that predates the `activity` field. This keeps older
 * call sites and tests valid while the canonical output stays fully populated.
 */
export type AnonymousStateInput = Omit<AnonymousState, 'activity'> & {
  activity?: AnonymousState['activity']
}

export function createDefaultConsent(): ConsentState {
  return {
    version: CONSENT_VERSION,
    necessary: true,
    analytics: false,
    externalMedia: false,
    marketing: false,
  }
}

export function parseConsentCookie(
  value: string | null | undefined,
): ConsentState {
  if (!value) return createDefaultConsent()

  try {
    return validateConsent(JSON.parse(decodeURIComponent(value)))
  } catch {
    return createDefaultConsent()
  }
}

/**
 * Parse the stored consent cookie into a CURRENT-version decision, or `null`
 * when there is none (missing, malformed, or recorded for an older version).
 */
export function parseStoredConsent(
  value: string | null | undefined,
): ConsentState | null {
  if (!value) return null
  try {
    return validateConsent(JSON.parse(decodeURIComponent(value)))
  } catch {
    return null
  }
}

export function serializeConsentCookie(consent: ConsentState): string {
  return encodeURIComponent(JSON.stringify(validateConsent(consent)))
}

export function createEmptyAnonymousState(): AnonymousState {
  return {
    saved: {
      lessons: [],
      courses: [],
      resources: [],
      posts: [],
      changelog: [],
    },
    likes: {
      lessons: [],
      comments: [],
      posts: [],
      resources: [],
      changelog: [],
    },
    progress: {},
    activity: {},
  }
}

/** Per-day caps mirror the server heartbeat caps so an anonymous local day can
 * never report more "learning" than a signed-in day would. */
export const MAX_MINUTES_PER_DAY = 240
export const MAX_LESSONS_PER_DAY = 35

export function parseAnonymousState(value: unknown): AnonymousState {
  try {
    return validateAnonymousState(value)
  } catch (error) {
    if (error instanceof Invalid) return createEmptyAnonymousState()
    throw error
  }
}

export function mergeAnonymousState(
  serverState: AnonymousStateInput,
  pendingState: AnonymousStateInput,
): AnonymousState {
  const server = parseAnonymousState(serverState)
  const pending = parseAnonymousState(pendingState)

  return {
    saved: {
      lessons: mergeUnique(server.saved.lessons, pending.saved.lessons),
      courses: mergeUnique(server.saved.courses, pending.saved.courses),
      resources: mergeUnique(server.saved.resources, pending.saved.resources),
      posts: mergeUnique(server.saved.posts, pending.saved.posts),
      changelog: mergeUnique(server.saved.changelog, pending.saved.changelog),
    },
    likes: {
      lessons: mergeUnique(server.likes.lessons, pending.likes.lessons),
      comments: mergeUnique(server.likes.comments, pending.likes.comments),
      posts: mergeUnique(server.likes.posts, pending.likes.posts),
      resources: mergeUnique(server.likes.resources, pending.likes.resources),
      changelog: mergeUnique(server.likes.changelog, pending.likes.changelog),
    },
    progress: mergeProgress(server.progress, pending.progress),
    activity: mergeActivity(server.activity, pending.activity),
  }
}

/**
 * Record learning activity for a single UTC day into the anonymous state. Used
 * by the lesson page heartbeat (minutes) and lesson completion (lessons) for
 * signed-out visitors. Both values are clamped per day so the local store can
 * never inflate a streak's stats beyond a plausible day of learning.
 */
export function recordDailyActivity(
  state: AnonymousStateInput,
  dateKey: string,
  delta: { minutesLearned?: number; lessonsCompleted?: number },
): AnonymousState {
  const parsed = parseAnonymousState(state)
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(dateKey)) return parsed
  const existing = parsed.activity[dateKey] ?? {
    minutesLearned: 0,
    lessonsCompleted: 0,
  }
  return {
    ...parsed,
    activity: {
      ...parsed.activity,
      [dateKey]: {
        minutesLearned: Math.min(
          MAX_MINUTES_PER_DAY,
          existing.minutesLearned + Math.max(0, delta.minutesLearned ?? 0),
        ),
        lessonsCompleted: Math.min(
          MAX_LESSONS_PER_DAY,
          existing.lessonsCompleted + Math.max(0, delta.lessonsCompleted ?? 0),
        ),
      },
    },
  }
}

/** Flatten the anonymous activity map into the `DailyActivity[]` shape consumed
 * by the shared streak-stats computation. */
export function activityToDailyList(
  state: AnonymousStateInput,
): Array<{ dateKey: string; minutesLearned: number; lessonsCompleted: number }> {
  const parsed = parseAnonymousState(state)
  return Object.entries(parsed.activity).map(([dateKey, value]) => ({
    dateKey,
    minutesLearned: value.minutesLearned,
    lessonsCompleted: value.lessonsCompleted,
  }))
}

export function toggleSavedTarget(
  state: AnonymousStateInput,
  group: SavedTargetGroup,
  targetId: string,
): AnonymousState {
  const parsed = parseAnonymousState(state)
  return {
    ...parsed,
    saved: {
      ...parsed.saved,
      [group]: toggleInSet(parsed.saved[group], targetId),
    },
  }
}

export function toggleLikedTarget(
  state: AnonymousStateInput,
  group: LikedTargetGroup,
  targetId: string,
): AnonymousState {
  const parsed = parseAnonymousState(state)
  return {
    ...parsed,
    likes: {
      ...parsed.likes,
      [group]: toggleInSet(parsed.likes[group], targetId),
    },
  }
}

export function setLessonProgress(
  state: AnonymousStateInput,
  lessonSlug: string,
  progress: Partial<AnonymousState['progress'][string]>,
): AnonymousState {
  const parsed = parseAnonymousState(state)
  const existing = parsed.progress[lessonSlug] ?? {
    watchedSeconds: 0,
    completed: false,
    lastViewedAt: 0,
  }
  const now = Date.now()

  return {
    ...parsed,
    progress: {
      ...parsed.progress,
      [lessonSlug]: {
        watchedSeconds: Math.max(
          existing.watchedSeconds,
          progress.watchedSeconds ?? 0,
        ),
        completed: progress.completed ?? existing.completed,
        lastViewedAt: Math.max(
          existing.lastViewedAt,
          progress.lastViewedAt ?? now,
        ),
      },
    },
  }
}

/**
 * A reason a signup nudge should appear, ranked by engagement. The component
 * maps this to localized copy (`common.signupPrompt.<reason>`), keeping the
 * logic locale-neutral instead of returning hardcoded German strings.
 */
export type SignupPromptReason = 'saves' | 'progress' | 'local'

export function getSignupPrompt(
  state: AnonymousStateInput,
): SignupPromptReason | null {
  const parsed = parseAnonymousState(state)
  if (parsed.saved.lessons.length >= 3) {
    return 'saves'
  }

  const completedLessons = Object.values(parsed.progress).filter(
    (item) => item.completed,
  ).length
  if (completedLessons >= 2) {
    return 'progress'
  }

  if (
    parsed.saved.lessons.length +
      parsed.saved.resources.length +
      parsed.likes.lessons.length +
      completedLessons >
    0
  ) {
    return 'local'
  }

  return null
}

function mergeUnique(left: string[], right: string[]): string[] {
  return Array.from(new Set([...left, ...right])).filter(Boolean)
}

function toggleInSet(values: string[], targetId: string): string[] {
  const normalized = targetId.trim()
  if (!normalized) return values
  return values.includes(normalized)
    ? values.filter((value) => value !== normalized)
    : [...values, normalized]
}

function mergeProgress(
  left: AnonymousState['progress'],
  right: AnonymousState['progress'],
): AnonymousState['progress'] {
  const merged: Partial<AnonymousState['progress']> = { ...left }

  for (const [lessonSlug, incoming] of Object.entries(right)) {
    const existing = merged[lessonSlug]
    if (!existing) {
      merged[lessonSlug] = incoming
      continue
    }

    merged[lessonSlug] = {
      watchedSeconds: Math.max(
        existing.watchedSeconds,
        incoming.watchedSeconds,
      ),
      completed: existing.completed || incoming.completed,
      lastViewedAt: Math.max(existing.lastViewedAt, incoming.lastViewedAt),
    }
  }

  return merged as AnonymousState['progress']
}

function mergeActivity(
  left: AnonymousState['activity'],
  right: AnonymousState['activity'],
): AnonymousState['activity'] {
  const merged: Partial<AnonymousState['activity']> = { ...left }

  for (const [dateKey, incoming] of Object.entries(right)) {
    const existing = merged[dateKey]
    if (!existing) {
      merged[dateKey] = incoming
      continue
    }
    // A day cannot be "learned twice": take the max of each side rather than the
    // sum, so re-running a migration or overlapping local/server days never
    // double-counts a day's minutes or lessons.
    merged[dateKey] = {
      minutesLearned: Math.max(
        existing.minutesLearned,
        incoming.minutesLearned,
      ),
      lessonsCompleted: Math.max(
        existing.lessonsCompleted,
        incoming.lessonsCompleted,
      ),
    }
  }

  return merged as AnonymousState['activity']
}
